import { Component, OnInit } from '@angular/core';
import { resolveImageUpload } from '../shared/utils/image-upload.util';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom, map, Observable, tap } from 'rxjs';
import { CustomProfileService } from '@proxy';
import { CustomUpdateProfileDto } from '@proxy/profiles/dto';
import { NotificationService } from '../shared/services/notification.service';
import { SelectedSommelierService } from '../shared/services/selected-sommelier.service';
import { ActivatedRoute } from '@angular/router';
import { StorageService } from '../shared/services/storage.service';
import { ARGENTINA_PROVINCES } from '../shared/constants/argentina-provinces';


@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  standalone: false
})
export class ProfileComponent implements OnInit {
  form!: FormGroup;
  isLoading = false;
  userData!: CustomUpdateProfileDto;

  // Imagen
  selectedFile: File | null = null;
  imagePreviewUrl: string | null = null;

  pickups$: Observable<Array<{ value: number, label: string }>> = new Observable();
  provinceOptions = ARGENTINA_PROVINCES.map(p => ({ value: p, label: p }));
  isSommelier: boolean;
  slug = '';

  constructor(
    private fb: FormBuilder,
    private profileService: CustomProfileService,
    private not: NotificationService,
    private slugService: SelectedSommelierService,
    private route: ActivatedRoute,
    private storageService: StorageService
  ) { }

  changePassword = false;

  toggleChangePassword() {
    this.changePassword = !this.changePassword;
  }

  async ngOnInit(): Promise<void> {
    this.slugService.getSlug$().subscribe(slug => {
      this.slug = slug;
    });

    await this.loadProfile();
  }

  private async loadProfile(): Promise<void> {
  try {
    this.userData = await firstValueFrom(this.profileService.getProfile());
    this.isSommelier = this.userData.roles.includes( "sommelier");

    this.buildForm();

    this.form.patchValue({
      name: this.userData.name,
      surname: this.userData.surname,
      documentNumber: this.userData.documentNumber,
      email: this.userData.email,
      phoneNumber: this.userData.phoneNumber,
      bio: this.userData.bio,
      country: this.userData.country,
      region: this.userData.region,
      city: this.userData.city,
      address: this.userData.address,
      province: this.userData.province,
      specialities: this.userData.specialities,
      certification: this.userData.certification,
      paymentAccountDetails: this.userData.paymentAccountDetails
    });

    if (this.userData.imageUrl) {
      const fileId = this.userData.fileAttachmentId;
      if (fileId) {
        this.storageService.getImage(fileId, 200, 300).subscribe(blob => {
          this.imagePreviewUrl = URL.createObjectURL(blob);
        });
      }
    }

    if (this.isSommelier) {
      this.pickups$ = this.profileService.getPickUps().pipe(
        map(list => list.map(p => ({ value: p.id, label: `${p.name} - ${p.address}` }))),
        tap(() => {
          this.form.patchValue({ pickupIds: this.userData.pickUpIds || [] });
        })
      );
    }
  } catch (err) {
    console.error('Error cargando perfil', err);
  }
}

  private buildForm(): void {
  this.form = this.fb.group({
    name: ['', Validators.required],
    surname: [''],
    documentNumber: [''],
    email: ['', [Validators.required, Validators.email]],
    phoneNumber: [''],
    address: [''],
    province: [''],
    currentPassword: [''],
    newPassword: [''],

     ...(this.isSommelier ? {
    bio: [''],
    country: [''],
    region: [''],
    city: [''],
    specialities: [''],
    certification: [''],
    paymentAccountDetails: [''],
  } : {})

  });

  if (this.isSommelier) {
    this.form.addControl('pickupIds', this.fb.control([], Validators.required));
  }

  this.form.get('email')?.disable();
  if (this.userData?.documentNumber) {
    this.form.get('documentNumber')?.disable();
  }

}


  onImageSelected(files: File[]): void {
    if (files && files.length > 0) {
      this.selectedFile = files[0];
      this.imagePreviewUrl = URL.createObjectURL(this.selectedFile);
    }
  }

  onImageValidationError(message: string): void {
    this.not.error(message, '::Error');
  }

  private async toUploadableFile(file: File): Promise<File> {
    // winepassport#398 — nombre y MIME coherentes y normalizados (ver image-upload.util.ts)
    const resolved = resolveImageUpload(file.name, file.type);
    if (!resolved) {
      throw new Error('Unsupported image type');
    }
    return new File([await file.arrayBuffer()], resolved.name, { type: resolved.type });
  }

  async onSave(): Promise<void> {
  if (this.form.invalid) return;

  this.isLoading = true;

  try {
    const formValue = this.form.getRawValue();
    const dto: CustomUpdateProfileDto = {
      ...this.userData,
      ...formValue,
      pickupIds: this.isSommelier ? formValue.pickupIds : []
    };

    if (this.selectedFile) {
      // winepassport#398: en mobile el File puede llegar sin nombre/MIME y el API no lo bindea como file
      const upload = await this.toUploadableFile(this.selectedFile);
      const formData = new FormData();
      formData.append('file', upload, upload.name);
      dto.fileAttachmentId = await firstValueFrom(this.profileService.uploadProfileImage(formData));
    }

    await firstValueFrom(this.profileService.updateProfile(dto));

    this.not.success("", '::ProfileUpdatedSuccessfully');
    window.location.reload();
  } catch (err) {
    this.not.error("", '::ProfileCouldNotBeUpdated');
  } finally {
    this.isLoading = false;
  }
}
}
